import { useState, useCallback } from "react";
import type { Task } from "@/features/tasks/types/task";
import { getTask } from "@/features/tasks/services/taskService";
import apiClient from "@/shared/services/apiClient";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
  deleteAttachmentFromAll,
  getAttachmentViewingUrl,
} from "@/shared/services/uploadService";
import { getMimeType } from "@/shared/utils/fileHelpers";
import { formatDisplayName } from "@/shared/utils/formatters";

export interface TaskComment {
  id: string;
  text: string;
  created_by: string;
  user_name: string;
  profile_pic_id?: string;
  created_at: string;
}

export interface TaskAttachment {
  id: string;
  display_name: string;
  file_type: string;
  file_size: number;
  created_at: string;
  created_by: string;
}

interface UseTaskDetailReturn {
  task: Task | null;
  comments: TaskComment[];
  attachments: TaskAttachment[];
  loadingTask: boolean;
  loadingComments: boolean;
  loadingAttachments: boolean;
  uploading: boolean;
  error: string | null;
  fetchTaskDetail: (id: string) => Promise<void>;
  addComment: (text: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  uploadAttachment: (file: File) => Promise<void>;
  deleteAttachment: (attachmentId: string) => Promise<void>;
  getAttachmentUrl: (attachmentId: string) => Promise<string>;
}

// Map raw backend comment row → TaskComment
const mapComment = (r: any): TaskComment => ({
  id:             String(r.comment_id),
  text:           r.content_text,
  created_by:     String(r.user_id),
  user_name:      r.user_name ?? formatDisplayName(r.last_name, r.first_name, r.middle_name),
  profile_pic_id: r.author_profile_pic_id ?? undefined,
  created_at:     r.created_at,
});

// Map raw backend attachment row → TaskAttachment
const mapAttachment = (r: any): TaskAttachment => ({
  id:           String(r.attachment_id),
  display_name: r.display_name,
  file_type:    r.file_type,
  file_size:    r.file_size,
  created_at:   r.created_at,
  created_by:   String(r.created_by ?? ""),
});

export function useTaskDetail(): UseTaskDetailReturn {
  const [task, setTask] = useState<Task | null>(null);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [loadingTask, setLoadingTask] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = useCallback(async (taskRowId: number) => {
    setLoadingComments(true);
    try {
      const { data } = await apiClient.get(`/comments/task/${taskRowId}`);
      const raw = Array.isArray(data) ? data : data.comments ?? [];
      setComments(raw.map(mapComment));
    } catch {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  const fetchAttachments = useCallback(async (taskId: string) => {
    setLoadingAttachments(true);
    try {
      const { data } = await apiClient.get(`/attachments/task/${taskId}`);
      const raw = Array.isArray(data) ? data : data.attachments ?? [];
      setAttachments(raw.map(mapAttachment));
    } catch {
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  }, []);

  const fetchTaskDetail = useCallback(
    async (id: string) => {
      setLoadingTask(true);
      setError(null);
      try {
        const t = await getTask(id);
        setTask(t);
        await Promise.all([fetchComments(t.row_id), fetchAttachments(id)]);
      } catch (err: any) {
        setError(err?.message ?? "Failed to load task");
      } finally {
        setLoadingTask(false);
      }
    },
    [fetchComments, fetchAttachments]
  );

  const addComment = useCallback(
    async (text: string) => {
      if (!task) return;
      await apiClient.post("/comments", {
        relation_type: "task",
        relation_id:   task.row_id,
        content_text:  text,
      });
      // Refetch so we get full user profile data on the new comment
      await fetchComments(task.row_id);
    },
    [task, fetchComments]
  );

  const deleteComment = useCallback(async (commentId: string) => {
    await apiClient.delete(`/comments/${commentId}`);
    setComments((prev) => prev.filter((c) => c.id !== commentId));
  }, []);

  const uploadAttachment = useCallback(
    async (file: File) => {
      if (!task) return;
      setUploading(true);
      try {
        const mimeType = getMimeType(file.name);
        const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(
          file.name,
          mimeType,
          "task",
          task.id
        );
        await uploadToS3Direct(uploadUrl, file, mimeType);
        const result = await confirmAttachmentInDB({
          relation_type: "task",
          relation_id:   task.id,
          s3_key:        s3Key,
          s3_bucket:     bucketName,
          display_name:  file.name,
          file_type:     mimeType,
          file_size:     file.size,
        });
        setAttachments((prev) => [...prev, mapAttachment(result)]);
      } finally {
        setUploading(false);
      }
    },
    [task]
  );

  const deleteAttachment = useCallback(async (attachmentId: string) => {
    await deleteAttachmentFromAll(attachmentId);
    setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  }, []);

  const getAttachmentUrl = useCallback(async (attachmentId: string) => {
    return getAttachmentViewingUrl(attachmentId);
  }, []);

  return {
    task,
    comments,
    attachments,
    loadingTask,
    loadingComments,
    loadingAttachments,
    uploading,
    error,
    fetchTaskDetail,
    addComment,
    deleteComment,
    uploadAttachment,
    deleteAttachment,
    getAttachmentUrl,
  };
}
